import { Suspense } from 'react'
import { Navigate, Outlet, useRoutes, type RouteObject } from 'react-router-dom'

import { AppLoadingScreen, NotFound } from 'src/components'
import { AccountPage } from 'src/features/account'
import {
  WorkspaceAppAuditLogDetailPage,
  WorkspaceAppCredentialDetailPage,
  WorkspaceAppDetailPage,
  WorkspaceAppLegacyTabRedirect,
  WorkspaceAppOutboundWebhookDetailPage
} from 'src/features/apps'
import {
  ConfirmMfaBackupCode,
  ConfirmMfaOtp,
  ConfirmRegister,
  EmailOtpLinkCallback,
  EmailOtpVerify,
  AppleCallback,
  AppleConnectCallback,
  AppleOrgConnectCallback,
  EntraIdConnectCallback,
  EntraIdOrgConnectCallback,
  GoogleCallback,
  GoogleConnectCallback,
  GoogleOrgConnectCallback,
  Invitation,
  Login,
  Organizations,
  Register,
  ResetPassword,
  ResetPasswordConfirm,
  TermsAndConditions
} from 'src/features/auth'
import { LayoutAuth } from 'src/features/auth/components'
import { Billing } from 'src/features/billing'
import { McpConsentPage } from 'src/features/connect'
import { OrganizationPage } from 'src/features/organization'
import {
  WorkspaceGroupsPage,
  WorkspaceJoinRequestsPage,
  WorkspaceMembersTabs,
  WorkspaceUserDetailPage,
  WorkspaceUsersPage
} from 'src/features/users'
import {
  WorkspaceAiConnectorsPage,
  WorkspaceAppsNewRedirect,
  WorkspaceAppsPage,
  WorkspaceAuditPage,
  WorkspaceBillingPage,
  WorkspaceCompanyGraphPage,
  WorkspaceDomainsPage,
  WorkspaceMembersSection,
  WorkspaceOrgDepartmentDetailPage,
  WorkspaceOrgDivisionDetailPage,
  WorkspaceOrgTeamDetailPage,
  WorkspaceOverviewPage,
  WorkspacePublicAccessCredentialDetailPage,
  WorkspacePublicAccessCredentialsPage,
  WorkspaceRoleDetailPage,
  WorkspaceRolesPage,
  WorkspaceSecurityPage,
  WorkspaceSettingsPage
} from 'src/features/workspaces'
import { WorkspaceAgentDetailPage } from 'src/features/workspaces/pages/WorkspaceAgentDetailPage'
import { WorkspaceAgentFormPage } from 'src/features/workspaces/pages/WorkspaceAgentFormPage'
import { WorkspaceAgentsPage } from 'src/features/workspaces/pages/WorkspaceAgentsPage'
import {
  useFeatureAgents,
  useFeatureBilling,
  useFeatureCompanyGraph,
  useFeatureDomains,
  useFeatureMcpConnectors,
  useFeaturePublicAccess,
  useFeatureRoles
} from 'src/flags'
import { AuthGuard } from 'src/hocs'
import { FocusedShell } from 'src/layout'
import { AppLayoutShell } from 'src/layout/AppLayoutShell'
import { WorkspacePermissionCode } from 'src/utils'

import { AccountLegacyRedirect } from './AccountLegacyRedirect'
import { DefaultLandingRedirect } from './DefaultLandingRedirect'
import { LegacyOrganizationRedirect } from './LegacyOrganizationRedirect'
import { paths } from './paths'
import { RequireWorkspacePermission } from './RequireWorkspacePermission'
import { WorkspaceAccessRoute } from './WorkspaceAccessRoute'
import { WorkspaceCreateRoute } from './WorkspaceCreateRoute'
import { WorkspaceFeatureRoute } from './WorkspaceFeatureRoute'
import { WorkspaceHomeRoute } from './WorkspaceHomeRoute'
import {
  RedirectWorkspaceSsoScimToSecurity,
  RedirectWorkspaceUsersToMembers,
  WorkspaceTrailingPathRedirect
} from './WorkspaceLegacyRedirects'
import { WorkspaceSelectRoute } from './WorkspaceSelectRoute'
import { WorkspacesListRoute } from './WorkspacesListRoute'

const workspaceDetailRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="home" replace /> },
  { path: 'home', element: <WorkspaceHomeRoute /> },
  { path: 'overview', element: <WorkspaceOverviewPage /> },
  {
    path: 'members',
    children: [
      {
        // Section shell (breadcrumb + "Users" title) applies to the list and
        // org detail pages only.
        element: <WorkspaceMembersSection />,
        children: [
          {
            // Tab strip over the two people screens. Separate pathnames keep
            // their list query params (search/page/ordering) apart.
            element: <WorkspaceMembersTabs />,
            children: [
              {
                index: true,
                element: (
                  <RequireWorkspacePermission
                    requiredPermission={WorkspacePermissionCode.USERS_VIEW}
                  >
                    <WorkspaceUsersPage />
                  </RequireWorkspacePermission>
                )
              },
              {
                // Groups and join requests answer to their own codes, so a
                // deep link into either has to be gated as well — the tab
                // strip only decides what is offered.
                path: 'groups',
                element: (
                  <RequireWorkspacePermission
                    requiredPermission={WorkspacePermissionCode.GROUPS_VIEW}
                  >
                    <WorkspaceGroupsPage />
                  </RequireWorkspacePermission>
                )
              },
              {
                path: 'join-requests',
                element: (
                  <RequireWorkspacePermission
                    requiredPermission={WorkspacePermissionCode.USERS_INVITE}
                  >
                    <WorkspaceJoinRequestsPage />
                  </RequireWorkspacePermission>
                )
              }
            ]
          },
          {
            path: 'divisions/:divisionId',
            element: <WorkspaceOrgDivisionDetailPage />
          },
          {
            path: 'departments/:departmentId',
            element: <WorkspaceOrgDepartmentDetailPage />
          },
          { path: 'teams/:teamId', element: <WorkspaceOrgTeamDetailPage /> }
        ]
      },
      // Renders its own WorkspacePageShell with breadcrumbLeaf — must stay
      // outside the section shell to avoid a duplicated breadcrumb.
      { path: 'people/:memberId', element: <WorkspaceUserDetailPage /> }
    ]
  },
  { path: 'organization', element: <OrganizationPage /> },
  {
    path: 'roles',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeatureRoles}>
        <Outlet />
      </WorkspaceFeatureRoute>
    ),
    children: [
      { index: true, element: <WorkspaceRolesPage /> },
      { path: ':roleId', element: <WorkspaceRoleDetailPage /> }
    ]
  },
  { path: 'security', element: <WorkspaceSecurityPage /> },
  { path: 'sso-scim', element: <RedirectWorkspaceSsoScimToSecurity /> },
  {
    path: 'domains',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeatureDomains}>
        <WorkspaceDomainsPage />
      </WorkspaceFeatureRoute>
    )
  },
  {
    path: 'public-access',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeaturePublicAccess}>
        <Outlet />
      </WorkspaceFeatureRoute>
    ),
    children: [
      { index: true, element: <WorkspacePublicAccessCredentialsPage /> },
      {
        path: ':credentialId',
        element: <WorkspacePublicAccessCredentialDetailPage />
      }
    ]
  },
  {
    path: 'ai-connectors',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeatureMcpConnectors}>
        <WorkspaceAiConnectorsPage />
      </WorkspaceFeatureRoute>
    )
  },
  {
    path: 'billing',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeatureBilling}>
        <WorkspaceBillingPage />
      </WorkspaceFeatureRoute>
    )
  },
  {
    path: 'agents',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeatureAgents}>
        <Outlet />
      </WorkspaceFeatureRoute>
    ),
    children: [
      { index: true, element: <WorkspaceAgentsPage /> },
      { path: 'new', element: <WorkspaceAgentFormPage /> },
      { path: ':agentId/edit', element: <WorkspaceAgentFormPage /> },
      { path: ':agentId', element: <WorkspaceAgentDetailPage /> }
    ]
  },
  {
    path: 'audit/:auditLogId',
    element: <WorkspaceAppAuditLogDetailPage />
  },
  { path: 'audit', element: <WorkspaceAuditPage /> },
  {
    path: 'graph',
    element: (
      <WorkspaceFeatureRoute useFlag={useFeatureCompanyGraph}>
        <WorkspaceCompanyGraphPage />
      </WorkspaceFeatureRoute>
    )
  },
  {
    path: 'apps',
    element: <Outlet />,
    children: [
      { index: true, element: <WorkspaceAppsPage /> },
      { path: 'new', element: <WorkspaceAppsNewRedirect /> },
      {
        path: ':appId/roles',
        element: <WorkspaceAppLegacyTabRedirect section="roles" />
      },
      {
        path: ':appId/credentials',
        element: <WorkspaceAppLegacyTabRedirect section="credentials" />
      },
      {
        path: ':appId/credentials/:credentialId',
        element: <WorkspaceAppCredentialDetailPage />
      },
      {
        path: ':appId/webhooks',
        element: <WorkspaceAppLegacyTabRedirect section="webhooks" />
      },
      {
        path: ':appId/outbound-webhooks/:webhookId',
        element: <WorkspaceAppOutboundWebhookDetailPage />
      },
      {
        path: ':appId/audit/:auditLogId',
        element: <WorkspaceAppAuditLogDetailPage />
      },
      {
        path: ':appId/audit',
        element: <WorkspaceAppLegacyTabRedirect section="activity" />
      },
      { path: ':appId', element: <WorkspaceAppDetailPage /> }
    ]
  },
  { path: 'users', element: <RedirectWorkspaceUsersToMembers /> },
  { path: 'settings', element: <WorkspaceSettingsPage /> },
  { path: '*', element: <WorkspaceTrailingPathRedirect /> }
]

const accountRoutes: RouteObject[] = [
  {
    path: paths.account.root.slice(1),
    element: <Outlet />,
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      { path: ':tab', element: <AccountPage /> }
    ]
  }
]

export const Router = () => {
  const routes: RouteObject[] = [
    {
      element: (
        <Suspense fallback={<AppLoadingScreen />}>
          <Outlet />
        </Suspense>
      ),
      children: [
        {
          path: 'authentication',
          element: (
            <LayoutAuth>
              <Outlet />
            </LayoutAuth>
          ),
          children: [
            {
              index: true,
              element: <Navigate to={paths.auth.login} replace />
            },
            {
              // Legacy URL — Terms now lives at the top-level public route.
              path: '/authentication/terms-and-conditions',
              element: <Navigate to={paths.termsAndConditions} replace />
            },
            { path: `${paths.auth.login}/*`, element: <Login /> },
            { path: paths.auth.register, element: <Register /> },
            {
              path: paths.auth.confirmRegister,
              element: <ConfirmRegister />
            },
            {
              // The invitation email's link — `buildWorkspaceInviteUrl` ends it
              // with a trailing slash before `?token=`, which React Router matches
              // to this path all the same.
              path: paths.auth.invitation,
              element: <Invitation />
            },
            { path: paths.auth.resetPassword, element: <ResetPassword /> },
            {
              path: paths.auth.resetPasswordConfirm,
              element: <ResetPasswordConfirm />
            },
            { path: paths.auth.confirmMfaOtp, element: <ConfirmMfaOtp /> },
            {
              path: paths.auth.confirmMfaBackupCode,
              element: <ConfirmMfaBackupCode />
            },
            { path: paths.auth.emailOtpVerify, element: <EmailOtpVerify /> },
            {
              path: paths.auth.emailOtpLink,
              element: <EmailOtpLinkCallback />
            },
            { path: paths.auth.organizations, element: <Organizations /> },
            {
              path: paths.auth.googleConnectCallback,
              element: <GoogleOrgConnectCallback />
            },
            {
              path: paths.auth.googleClientCallback,
              element: <GoogleCallback />
            },
            {
              path: paths.auth.googleClientConnectCallback,
              element: <GoogleConnectCallback />
            },
            {
              path: paths.auth.entraIdConnectCallback,
              element: <EntraIdConnectCallback />
            },
            {
              path: paths.auth.entraIdOrgConnectCallback,
              element: <EntraIdOrgConnectCallback />
            },
            {
              path: paths.auth.appleClientCallback,
              element: <AppleCallback />
            },
            {
              path: paths.auth.appleClientConnectCallback,
              element: <AppleConnectCallback />
            },
            {
              path: paths.auth.appleOrgConnectCallback,
              element: <AppleOrgConnectCallback />
            }
            // { path: paths.auth.onboarding, element: <Onboarding /> }
          ]
        },

        // Legal pages are public and live outside the /authentication tree.
        {
          path: paths.termsAndConditions.slice(1),
          element: <TermsAndConditions />
        },

        {
          path: '/',
          element: (
            <AuthGuard>
              <Outlet />
            </AuthGuard>
          ),
          children: [
            // Workspace creation owns its chrome: the first-ever workspace is
            // framed by a focused standalone shell (no sidebar), any later
            // creation keeps the full app shell. The gate decides.
            {
              path: `${paths.workspaces.root.slice(1)}/new`,
              element: <WorkspaceCreateRoute />
            },

            // The post-login workspace chooser is pre-workspace, so it owns the
            // same focused standalone chrome (no sidebar) as the create flow.
            // Static `select` ranks above the dynamic `:id` route below.
            {
              path: `${paths.workspaces.root.slice(1)}/select`,
              element: <WorkspaceSelectRoute />
            },

            // MCP consent: Core sends the browser here mid-OAuth. No workspace
            // frames it (the user picks one on the page), so focused chrome.
            {
              path: 'connect/consent/:requestId',
              element: (
                <FocusedShell>
                  <McpConsentPage />
                </FocusedShell>
              )
            },

            // The workspace subtree owns its chrome via WorkspaceAccessRoute:
            // it renders the full app shell when the profile can view the
            // workspace, or the focused standalone shell (no sidebar) for the
            // "wrong account" screen when the profile isn't a member. Kept a
            // sibling of the shell group below so the no-access screen can drop
            // the sidebar entirely.
            {
              path: `${paths.workspaces.root.slice(1)}/:id`,
              element: <WorkspaceAccessRoute />,
              children: workspaceDetailRoutes
            },

            // Everything else lives inside the standard app shell.
            {
              element: (
                <AppLayoutShell>
                  <Outlet />
                </AppLayoutShell>
              ),
              children: [
                { index: true, element: <DefaultLandingRedirect /> },
                {
                  path: paths.workspaces.root.slice(1),
                  element: <WorkspacesListRoute />
                },
                {
                  path: paths.legacyOrganizations.root.slice(1),
                  element: <LegacyOrganizationRedirect />
                },
                {
                  path: `${paths.legacyOrganizations.root.slice(1)}/:id`,
                  element: <LegacyOrganizationRedirect />
                },
                {
                  path: `${paths.legacyOrganizations.root.slice(1)}/:id/:tab`,
                  element: <LegacyOrganizationRedirect />
                },

                ...accountRoutes,

                // Legacy /profile/:userId/* routes redirect into /account/*.
                {
                  path: 'profile/:userId',
                  element: <AccountLegacyRedirect />
                },
                {
                  path: 'profile/:userId/*',
                  element: <AccountLegacyRedirect />
                },

                {
                  path: paths.billing.slice(1),
                  element: <Billing />
                }
              ]
            }
          ]
        },

        {
          path: '*',
          element: <NotFound />
        }
      ]
    }
  ]

  return useRoutes(routes)
}
